import { Injectable, SecurityContext, inject } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";

import { MatDialog, MatDialogConfig } from "@angular/material/dialog";

import { Address, IIntent } from "@tylertech/tyler-api-types";

import
{
    AddressDialogComponent,
    AddressDialogData,
    AlertDialogComponent,
    AlertDialogData,
    CommentDialogComponent,
    CommentDialogData,
    ConfirmDialogComponent,
    ConfirmDialogData,
    DIALOG_CANCEL,
    DragAndDropDialogComponent,
    DragAndDropDialogData,
    DragAndDropGroupDialogComponent,
    DragAndDropGroupDialogData,
    IntentActionDialogComponent,
    IntentActionDialogData,
    PromptDialogComponent,
    PromptDialogData,
    SaveDialogComponent,
    SaveDialogData,
    SavingDialogComponent,
    SavingDialogData,
} from "../components";
import { firstValueFrom } from "rxjs";

@Injectable({ providedIn: "root" })
export class CommonDialogsService
{
    private readonly domSanitizer = inject(DomSanitizer);
    private readonly matDialog = inject(MatDialog);


    // FIXME - only used in apps/requisitions; pull up to the that layer.
    /**
     * TODO - Why are we passing in a function when we are using promises? Downstream should just `.then` on the returned Promise.
     * @deprecated - this and it's usages needs to be refactored.
     */
    showAddress(model: Address, onSave: (address: Address) => Promise<boolean>): Promise<void>
    {
        const dialogConfig: MatDialogConfig<AddressDialogData> = {
            data: {
                model,
                onSave
            }
        };

        const dialogRef = this.matDialog.open(AddressDialogComponent, dialogConfig);
        return firstValueFrom(dialogRef.afterClosed());
    }

    /**
     *
     * @param message
     * @param title
     * @returns
     * @deprecated - use `showAlertMessage`
     */
    showAlertHtmlMessage(message: string, title: string = "Error", ariaLabel: string = "Error", okLabel?: string): Promise<void>
    {
        return this.showAlertMessage(message, title, ariaLabel, okLabel);
    }

    /**
     *
     * @param message
     * @param title
     * @param _ariaLabel
     * @returns
     */
    showAlertMessage(
        message: string,
        title: string = "Error",
        ariaLabel: string = "Error",
        okLabel?: string
    ): Promise<void>
    {
        message = message.replaceAll("\r\n", "<br>")
            .replaceAll("\n", "<br>")
            .replaceAll("\r", "<br>");
        const dialogConfig: MatDialogConfig<AlertDialogData> = {
            disableClose: false,
            data: {
                title,
                message: this.domSanitizer.sanitize(SecurityContext.HTML, message),
                okLabel: okLabel
            },
            ariaLabel: ariaLabel
        };

        const dialogRef = this.matDialog.open(AlertDialogComponent, dialogConfig);
        return dialogRef.afterClosed().toPromise();
    }

    showCancelDialog(): Promise<void>
    {
        return this.showConfirmationMessage("Are you sure? All work will be lost.", "Confirm Cancel");
    }

    showComment(label: string, nextIntent?: IIntent): Promise<string>
    {
        const dialogConfig: MatDialogConfig<CommentDialogData> = {
            data: {
                label,
                nextIntent
            }
        };

        const dialogRef = this.matDialog.open(CommentDialogComponent, dialogConfig);
        return dialogRef.afterClosed().toPromise()
            .then(r =>
            {
                if (r === "DIALOG_CANCEL")
                {
                    return Promise.reject();
                }

                return r;
            });
    }

    /**
     *
     * @param message
     * @param title
     * @param ariaLabel - NO LONGER USED
     * @returns
     * @deprecated - use `showConfirmationMessage`
     */
    showConfirmationHtmlMessage(
        message: string,
        title: string = "Confirm",
        _ariaLabel: string = "Confirm"
    ): Promise<void>
    {
        return this.showConfirmationMessage(message, title);
    }

    /**
     *
     * @param message
     * @param title
     * @param ariaLabel
     * @returns
     */
    showConfirmationMessage(message: string, title: string = "Confirm", okLabel?: string, cancelLabel?: string, ariaLabel: string = "Confirm"): Promise<void>
    {
        message = message.replaceAll("\r\n", "<br>")
            .replaceAll("\n", "<br>")
            .replaceAll("\r", "<br>");
        const dialogConfig: MatDialogConfig<ConfirmDialogData> = {
            disableClose: false,
            data: {
                title,
                message: this.domSanitizer.sanitize(SecurityContext.HTML, message),
                okLabel,
                cancelLabel
            },
            ariaLabel: ariaLabel
        };

        const dialogRef = this.matDialog.open(ConfirmDialogComponent, dialogConfig);
        return firstValueFrom(dialogRef.afterClosed())
            .then(accepted =>
            {
                if (accepted)
                {
                    return;
                }

                return Promise.reject();
            });
    }

    showDragAndDropDialog<T extends object = Record<string, unknown>>(
        groups: Array<Array<T>>,
        display: keyof T & string,
        subtitleMember: keyof T & string,
        dialogTitle: string,
        dialogSubtitle: string,
        groupTitles?: Array<string>
    ): Promise<Array<Array<T>>>
    {
        const dialogConfig: MatDialogConfig<DragAndDropDialogData<T>> = {
            data: {
                groups,
                display,
                subtitleMember,
                dialogTitle,
                dialogSubtitle,
                ...(groupTitles !== undefined && { groupTitles })
            }
        };

        const dialogRef = this.matDialog.open(DragAndDropDialogComponent, dialogConfig);
        return dialogRef.afterClosed().toPromise();
    }

    showDragAndDropGroupDialog<T extends object = Record<string, unknown>>(
        groups: Array<Array<T>>,
        groupProperty: keyof T & string,
        display: keyof T & string,
        subtitleMember: keyof T & string,
        dialogTitle: string,
        dialogSubtitle: string,
        sequenceProperty: keyof T & string,
        groupTitles?: Array<string>
    ): Promise<Array<Array<T>>>
    {
        const dialogConfig: MatDialogConfig<DragAndDropGroupDialogData<T>> = {
            data: {
                groups,
                groupProperty,
                display,
                subtitleMember,
                dialogTitle,
                dialogSubtitle,
                sequenceProperty,
                ...(groupTitles !== undefined && { groupTitles })
            }
        };

        const dialogRef = this.matDialog.open(DragAndDropGroupDialogComponent, dialogConfig);
        return dialogRef.afterClosed().toPromise();
    }

    showNavigationWithChangesDialog(): Promise<void>
    {
        return this.showConfirmationMessage(
            "There are unsaved changes, do you want to leave the page?",
            "Unsaved Changes");
    }

    /**
     *
     * @param message
     * @param title
     * @param initialValue
     * @param _ariaLabel - NO LONGER USED
     * @returns
     * @deprecated - use `showPrompt`
     */
    showHtmlPrompt(
        message: string,
        title: string,
        initialValue?: string,
        _ariaLabel: string = "Prompt"
    ): Promise<string>
    {
        return this.showPrompt(message, title, initialValue, _ariaLabel);
    }

    /**
     *
     * @param message
     * @param title
     * @param initialValue
     * @param _ariaLabel - NO LONGER USED
     * @returns
     */
    showPrompt(message: string, title: string, initialValue?: string, _ariaLabel: string = "Prompt"): Promise<string>
    {
        message = message.replaceAll("\r\n", "<br>")
            .replaceAll("\n", "<br>")
            .replaceAll("\r", "<br>");
        const dialogConfig: MatDialogConfig<PromptDialogData> = {
            data: {
                title,
                message,
                initialValue,
            }
        };

        const dialogRef = this.matDialog.open(PromptDialogComponent, dialogConfig);
        return dialogRef.afterClosed().toPromise();
    }

    /**
     * @deprecated This is a very difficult pattern to work with. We have to find a different solution.
     */
    showSavingDialog<T>(entity: T, onSave: (entity: T) => Promise<T | string>): Promise<void>
    {
        // const DialogComponent = new SavingDialogComponent(entity, onSave);
        // return Promise.resolve(this.$mdDialog.show(DialogComponent));
        const dialogConfig: MatDialogConfig<SavingDialogData> = {
            data: {
                entity,
                onSave
            }
        };

        const dialogRef = this.matDialog.open(SavingDialogComponent, dialogConfig);
        return dialogRef.afterClosed().toPromise();
    }

    /**
     * @deprecated This is a very difficult pattern to work with. We have to find a different solution.
     */
    showWaitingDialog<T>(
        title: string,
        action: () => Promise<T>,
        getSuccessMessageFn: (result: T) => string,
        getErrorMessageFn: (error) => string
    ): Promise<T>
    {
        const dialogConfig: MatDialogConfig<SaveDialogData> = {
            data: {
                action,
                getErrorMessageFn,
                getSuccessMessageFn,
                title
            }
        };

        const dialogRef = this.matDialog.open(SaveDialogComponent, dialogConfig);
        return dialogRef.afterClosed().toPromise();
    }

    showIntentAction(data: IntentActionDialogData): Promise<void>
    {
        const dialogConfig: MatDialogConfig<IntentActionDialogData> = {
            data
        };

        const dialogRef = this.matDialog.open(IntentActionDialogComponent, dialogConfig);
        return dialogRef.afterClosed().toPromise();

    }

    dialogCanceled(result: string): boolean
    {
        return result === DIALOG_CANCEL;
    }
}
